import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import InstallPromptTrigger from '@alltrails/amplitude/enums/InstallPromptTrigger';
import logContinueInBrowserClicked from '@alltrails/amplitude/events/logContinueInBrowserClicked';
import logInstallPromptDismissed from '@alltrails/amplitude/events/logInstallPromptDismissed';
import Button, { ButtonProps } from '@alltrails/denali/components/Button';
import type { IconDefinition } from '@alltrails/denali/components/IconRenderer';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useDeeplink from '../../hooks/useDeeplink';
import { deepLinkRedirect } from '../../utils';
import AppPromotion from '../AppPromotion';
import BaseInstallInterstitial from '../BaseInstallInterstitial';
import FeatureContext from './FeatureContext';
import styles from './MiniInstallInterstitial.module.scss';

type BaseMiniInstallInterstitialProps = {
  backgroundClassName?: string;
  contentsClassName?: string;
  isOpen?: boolean;
  onRequestClose?: () => void;
  promptLocation: InstallPromptLocation;
  displayTrigger?: InstallPromptTrigger;
  deeplinkData?: DeeplinkData;
  customAppPath?: string;
  buttonText?: ReactNode;
  secondaryAction?: {
    text: ReactNode;
    onClick: () => void;
    icon?: ButtonProps['icon'];
  };
  shouldHideCloseButton?: boolean;
  showContinueInBrowser?: boolean;
  clickOutsideDeactivates?: boolean;
};

type FeatureContextVariantProps = BaseMiniInstallInterstitialProps & {
  variant: 'feature-context';
  trailDetails: TrailDetails;
  featureIcon: IconDefinition<'size'>;
  heading?: ReactNode;
  description?: ReactNode;
};

type AppInfoVariantProps = BaseMiniInstallInterstitialProps & {
  variant?: 'app-info';
  trailDetails?: never;
};

type MiniInstallInterstitialProps = FeatureContextVariantProps | AppInfoVariantProps;

const MiniInstallInterstitial = ({
  backgroundClassName,
  contentsClassName,
  isOpen = false,
  onRequestClose,
  promptLocation,
  displayTrigger,
  deeplinkData,
  customAppPath,
  buttonText,
  secondaryAction,
  shouldHideCloseButton = false,
  showContinueInBrowser = false,
  clickOutsideDeactivates = true,
  ...variantProps
}: MiniInstallInterstitialProps) => {
  const intl = useIntl();
  const getDeeplink = useDeeplink({ deeplinkData, customAppPath, isOpen, promptType: 'interstitial', displayTrigger });

  const handleContinueInApp = () => {
    deepLinkRedirect(getDeeplink(), 'interstitial', promptLocation, 'native_with_appsflyer_link', displayTrigger);
  };

  const handleContinueInBrowser = () => {
    logContinueInBrowserClicked({
      install_prompt_location: promptLocation,
      install_prompt_source: 'native_with_appsflyer_link',
      install_prompt_type: 'interstitial'
    });
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_source: 'native_with_appsflyer_link',
      install_prompt_trigger: displayTrigger,
      install_prompt_type: 'interstitial'
    });
    onRequestClose?.();
  };

  return (
    <BaseInstallInterstitial
      backgroundClassName={backgroundClassName}
      clickOutsideDeactivates={clickOutsideDeactivates}
      displayTrigger={displayTrigger}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      promptLocation={promptLocation}
      shouldHideCloseButton={shouldHideCloseButton}
    >
      <div
        className={classNames(styles.content, contentsClassName, {
          [styles.featureContextContent]: variantProps.variant === 'feature-context',
          [styles.topPadding]: variantProps.variant === 'feature-context' && shouldHideCloseButton
        })}
      >
        {variantProps.variant === 'feature-context' ? (
          <FeatureContext
            trailDetails={variantProps.trailDetails}
            heading={variantProps.heading}
            description={variantProps.description}
            featureIcon={variantProps.featureIcon}
          />
        ) : (
          <AppPromotion heading={<FormattedMessage defaultMessage="AllTrails is better in the app" />} />
        )}

        <div className={classNames(styles.actions, { [styles.appInfoActions]: showContinueInBrowser })}>
          {secondaryAction && (
            <Button
              icon={secondaryAction.icon}
              onClick={secondaryAction.onClick}
              text={secondaryAction.text}
              variant="secondary"
              width="fullOnMobile"
            />
          )}
          <Button
            onClick={handleContinueInApp}
            text={buttonText || intl.formatMessage({ defaultMessage: 'Continue in app' })}
            variant="primary"
            width="fullOnMobile"
          />
          {showContinueInBrowser ? (
            <Button
              onClick={handleContinueInBrowser}
              text={<FormattedMessage defaultMessage="Continue in browser" />}
              variant="ghost"
              width="fullOnMobile"
            />
          ) : null}
        </div>
      </div>
    </BaseInstallInterstitial>
  );
};

export default MiniInstallInterstitial;
